import angular, { IScope } from 'angular'
import ngRedux from 'ng-redux'
import { PAYMENT_TYPES } from '@vend/business-components'

import { vn } from 'vend-number'
import { STATES } from '../../../../lib/routes'
import { RegisterManager } from '../../managers/register-manager'
import { RetailerSettingsManager } from '../../managers/retailer-settings-manager'
import { $state } from '../../ng-compat/imports'
import { RootState } from '../../state/store'
import {
  ReduxCurrentSale,
  selectLineItems,
  selectSaleNote,
  selectSaleSummaryFields,
} from '../../state/slices/currentSale/currentSaleSlice'
import { Payment } from '../../models/payment'
import { HIDDEN_PAYMENT_TYPES } from '../../components/payments/integrated-payment/integrated-payment-types.constant'
import { SaleManager } from '../../managers/sale-manager'
import {
  getCashDiscountedSaleTotal,
  getChangeAmountForDisplay,
  getOutstandingBalanceAfterChange,
  getSurchargeLabel,
  isAccountBalancePaymentAdded,
  isQuote,
} from '../../functions/sale'
import { getBasketSize } from '../../functions/lineItem'
import { hasChange } from '../../functions/payments'
import { EcomCustomCharge } from '../../models/sale'

/**
 * The fields that are required for the sale summary view
 */
export type SaleSummaryFields = Pick<
  ReduxCurrentSale,
  | 'finals'
  | 'discount'
  | 'status'
  | 'totals'
  | 'payAccountBalance'
  | 'change'
  | 'payments'
>

/**
 * =======================================
 * SALE CONFIRMATION CONTROLLER
 * =======================================
 */
export class SaleSummaryCtrl {
  public isStoreCreditEnabled: boolean
  public isTaxInclusive: boolean
  public note: string = ''
  public sale: Readonly<SaleSummaryFields>

  constructor(
    private saleManager: SaleManager,
    private registerManager: RegisterManager,
    private retailerSettingsManager: RetailerSettingsManager,
    private $scope: IScope,
    private $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'
    this.isTaxInclusive = this.registerManager.isTaxInclusive()
    this.isStoreCreditEnabled = this.retailerSettingsManager.isStoreCreditEnabled()
    this.saleManager.init()

    // typescript doesn't realise that sale is set in the connect function below.
    // This is a workaround to get the initial value of sale to controller then it will
    // be updated by the connect function
    this.sale = selectSaleSummaryFields(this.$ngRedux.getState())

    // initiate redux
    const disconnectRedux = this.$ngRedux.connect((state: RootState) => ({
      note: selectSaleNote(state),
      sale: selectSaleSummaryFields(state),
    }))(this)
    this.$scope.$on('$destroy', disconnectRedux)
  }

  /**
   * Close the payment screen and return to the current sale. Do not close the sale.
   *
   * @method closePaymentScreen
   */
  public closePaymentScreen() {
    $state.go(STATES.SALE)
  }

  /**
   * Returns true if this payment line should be hidden from receipts.
   *
   * @return true if should be hidden
   */
  public shouldHidePayment(payment: Payment): boolean {
    return HIDDEN_PAYMENT_TYPES.indexOf(payment.paymentType.type_id) >= 0
  }

  /**
   * Returns true if this payment line is of type foreign currency
   *
   * @return true if foreign currency
   */
  public isForeignCurrency(payment: Payment): boolean {
    return PAYMENT_TYPES.CURRENCY === payment.paymentType.type_id
  }

  /**
   * Determine if the sale has multiple tax groups
   *
   * @return {Boolean} Returns true if the sale has a change amount, false otherwise
   */
  public hasMultipleTaxGroups(): boolean {
    return Object.keys(this.sale.finals.taxes).length > 1
  }

  /**
   * Determine if the sale is a quote
   *
   * @return {Boolean} Returns true if the sale has a change amount, false otherwise
   */
  public isQuote(): boolean {
    return isQuote(this.sale.status)
  }

  /**
   * Determines if the sale has any tips associated with its past payments (from saved sale) and currently made payments
   * @returns true if the sale has any tips in all its payments
   */
  public hasTip(): boolean {
    return vn(this.sale.finals.tip).gt(0)
  }

  /**
   * Determines if the sale has any surcharges associated with its past payments (from saved sale) and currently made payments
   * @returns true if the sale has any surcharges in all its payments
   */
  public hasSurcharge(): boolean {
    return !vn(this.sale.finals.surchargeDisplayProperties?.surcharge).eq(0)
  }

  public getSurchargeLabel(): string {
    return getSurchargeLabel(this.sale)
  }

  public hasEcomCustomCharges(): boolean {
    return !vn(this.sale.finals.ecomCustomCharges?.totalIncl).eq(0)
  }

  public isEcomCustomChargeTaxed(ecomCustomCharge: EcomCustomCharge): boolean {
    return !vn(ecomCustomCharge.totalTax).eq(0)
  }

  public getCashDiscountedSaleTotal(): number {
    return getCashDiscountedSaleTotal(this.sale)
  }

  public getBasketSize(): number {
    const lineItems = selectLineItems(this.$ngRedux.getState<RootState>())
    return getBasketSize(lineItems)
  }

  public isAccountBalancePaymentAdded(): boolean {
    return isAccountBalancePaymentAdded(this.sale.payAccountBalance)
  }

  public hasChange(): boolean {
    return hasChange(this.sale.change)
  }

  public getOutstandingBalanceAfterChange(): number {
    return getOutstandingBalanceAfterChange(this.sale)
  }

  public getChangeAmountForDisplay(): number {
    return getChangeAmountForDisplay(this.sale)
  }
}

/**
 * Controller for the sale summary view
 *
 * @module webregister.controllers.SaleSummaryCtrl
 */
angular
  .module('webregister.controllers')
  .controller('SaleSummaryCtrl', SaleSummaryCtrl)
